import { Timestamp } from 'firebase/firestore';
import { formatDistanceToNowStrict, format } from 'date-fns';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

// Tailwind CSS class merger
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Date/time helpers
export const isTs = (v) => v instanceof Timestamp;
export const toDate = (v) => (isTs(v) ? v.toDate() : v ? new Date(v) : null);

export function toDateAny(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (isTs(v)) return v.toDate();
  if (typeof v === 'number') return new Date(v);
  if (typeof v === 'string') {
    const d = new Date(v);
    return isNaN(d) ? null : d;
  }
  if (typeof v === 'object') {
    if (typeof v.seconds === 'number') {
      return new Date(v.seconds * 1000 + (v.nanoseconds ? v.nanoseconds / 1e6 : 0));
    }
    if (v.start) return toDateAny(v.start);
    if (v.date) return toDateAny(v.date);
    if (v.when) return toDateAny(v.when);
  }
  return null;
}

export function prettyAgo(ts) {
  const d = toDate(ts);
  if (!d) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days >= 10) return format(d, 'M/d/yy');
  return formatDistanceToNowStrict(d)
    .replace(' minutes', 'm').replace(' minute', 'm')
    .replace(' hours', 'h').replace(' hour', 'h')
    .replace(' days', 'd').replace(' day', 'd')
    .replace(' weeks', 'w').replace(' week', 'w')
    .replace(' months', 'mo').replace(' month', 'mo');
}

export function formatDayTime(value) {
  const d = toDateAny(value);
  if (!d) return null;
  return `${format(d, 'EEE, M/d')} • ${format(d, 'p')}`;
}

// Data processing helpers
export function toStr(val) {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (typeof val === 'object') {
    if (typeof val.text === 'string') return val.text;
    if (typeof val.title === 'string') return val.title;
    if (typeof val.name === 'string') return val.name;
    if (typeof val.address === 'string') return val.address;
    return '';
  }
  try {
    return String(val);
  } catch {
    return '';
  }
}

export function normalizeLocation(val) {
  if (!val) return null;
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    if (val.name || val.address) return [val.name, val.address].filter(Boolean).join(' • ');
    if (typeof val.lat === 'number' && typeof val.lng === 'number') return `${val.lat.toFixed(4)}, ${val.lng.toFixed(4)}`;
    if (val.city) return String(val.city);
  }
  return toStr(val);
}

export function alertPresentation(n, teamCache, scrimCache) {
  const d = n?.data || {};
  const type = (n.type || d.type || '').toLowerCase();
  const subtype = (n.subtype || d.subtype || '').toLowerCase();

  const actorId = n.actorTeamId || d.actorTeamId || d.fromTeamId || null;
  const targetId = n.teamId || d.teamId || d.toTeamId || null;
  const oppId = d.opponentTeamId || null;

  const actorName = actorId ? (teamCache[actorId]?.name || 'Team') : null;
  const targetName = targetId ? (teamCache[targetId]?.name || 'Your team') : null;
  const oppName = oppId ? (teamCache[oppId]?.name || 'Opponent') : null;

  const scrimmageId = n.scrimmageId || d.scrimmageId || null;
  const scrim = scrimmageId ? scrimCache[scrimmageId] : null;

  const when = d.when || d.date || d.kickoff || scrim?.when || null;
  const whenFmt = when ? formatDayTime(when) : null;

  const locationRaw = d.location ?? d.field ?? d.city ?? scrim?.location ?? null;
  const location = normalizeLocation(locationRaw);

  const age = toStr(d.ageGroup ?? d.age ?? scrim?.ageGroup ?? '');
  const level = toStr(d.level ?? d.skill ?? scrim?.level ?? '');

  let title = toStr(n.title) || 'Notification';
  if (/interest/.test(type) || /interest/.test(subtype)) {
    title = `${actorName || 'A team'} is interested in your team`;
  } else if (/confirmed/.test(type) || /confirm/.test(subtype)) {
    title = `Match confirmed`;
  } else if (/message/.test(type)) {
    title = `${actorName || 'A team'} sent a message`;
  } else if (/invite/.test(type)) {
    title = `${actorName || 'A team'} invited you`;
  }

  const parts = [];
  if (actorName) parts.push(`From ${actorName}`);
  if (targetName && (!actorName || targetName !== actorName)) parts.push(`For ${targetName}`);
  if (scrim?.title) parts.push(`Match: ${toStr(scrim.title)}`);
  const context = parts.join(' • ');

  const chips = [];
  if (whenFmt) chips.push({ key: 'when', text: whenFmt });
  if (location) chips.push({ key: 'loc', text: location });
  if (age) chips.push({ key: 'age', text: age });
  if (level) chips.push({ key: 'lvl', text: level });
  if (oppName && (!actorName || oppName !== actorName)) {
    chips.push({ key: 'opp', text: oppName });
  }
  if (scrim?.status && String(scrim.status).toLowerCase() === 'confirmed') {
    chips.push({ key: 'status', text: 'Confirmed' });
  }

  return { titleLong: title, context, chips };
}